import { useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';

import { METRIC_GRID_COMPACT_HEIGHT } from '../utils/metricGridLayout';

/** Hysteresis avoids flicker when expanded bottom crosses the scroll top edge. */
function shouldShowCompact(
  root: HTMLElement,
  target: HTMLElement,
  currentlyVisible: boolean,
): boolean {
  const rootTop = root.getBoundingClientRect().top;
  const targetBottom = target.getBoundingClientRect().bottom;
  if (currentlyVisible) {
    return targetBottom <= rootTop + 6;
  }
  return targetBottom <= rootTop - 2;
}

/**
 * Show compact metrics only after the expanded grid has fully left the viewport.
 * Uses bounding rects (reliable inside overflow scroll containers).
 */
export function useCompactMetricsVisible(
  scrollRootRef: RefObject<HTMLElement | null>,
  expandedRef: RefObject<HTMLElement | null>,
): boolean {
  const [visible, setVisible] = useState(false);
  const visibleRef = useRef(false);

  useEffect(() => {
    const root = scrollRootRef.current;
    const target = expandedRef.current;
    if (!root || !target) return;

    const update = () => {
      const next = shouldShowCompact(root, target, visibleRef.current);
      if (next === visibleRef.current) return;

      const delta = next ? METRIC_GRID_COMPACT_HEIGHT : -METRIC_GRID_COMPACT_HEIGHT;
      root.scrollTop += delta;

      visibleRef.current = next;
      setVisible(next);
    };

    root.addEventListener('scroll', update, { passive: true });
    update();

    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(target);
    resizeObserver.observe(root);

    return () => {
      root.removeEventListener('scroll', update);
      resizeObserver.disconnect();
    };
  }, [scrollRootRef, expandedRef]);

  return visible;
}
